import { MemoryHistory, History } from 'history'
import { getHistoryInstance, getLocationUrl } from 'hooks/useHistory'
import { useEffect } from 'react'
import { isIFrame } from 'utils/isIFrame'
import { useIsPwa } from 'utils/isPwa'
import { reloadPage } from 'utils/location'
import { type TabEntry, type TabRootStore } from './TabRootStore'

// Message types that can be sent across the iframe boundary.
export type MessageToRoot =
  | { type: 'AddTab'; url: string; title?: string; switchToTab?: boolean }
  | { type: 'CloseCurrentTab' }
  | { type: 'SetCurrentTab'; tab: number }
  | { type: 'GoToNextOrPreviousTab'; nextTab: boolean }
  | { type: 'SetTabTitle'; title: string }
  | { type: 'SetTabHref'; href: string }
  | { type: 'UpdateHistoryState'; canGoBack: boolean; canGoForward: boolean }
  | { type: 'LoadingFinished' }

export const sendMessageToRoot = (msg: MessageToRoot) => {
  window.parent.postMessage(msg, '*')
}

function findTabIframe(tabEntry: TabEntry) {
  const iframes = Array.from(document.querySelectorAll('iframe'))
  for (const iframe of iframes) {
    if (iframe.dataset.uuid === tabEntry.uuid) {
      return iframe
    }
  }
}

const keyboardShortcutHandler = (tabRoot?: TabRootStore) => (e: KeyboardEvent) => {
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'w') {
    if (tabRoot) tabRoot.closeCurrentTab()
    else sendMessageToRoot({ type: 'CloseCurrentTab' })
  } else if ((e.ctrlKey || e.metaKey) && e.key.match(/^\d$/)) {
    const index = Number(e.key) - 1
    if (tabRoot) tabRoot.setCurrentTab(index)
    else sendMessageToRoot({ type: 'SetCurrentTab', tab: index })
  } else if ((e.ctrlKey || e.metaKey) && e.key === 'Tab') {
    const nextTab = !e.shiftKey
    if (tabRoot) tabRoot.goToNextOrPreviousTab(nextTab)
    else sendMessageToRoot({ type: 'GoToNextOrPreviousTab', nextTab })
  } else if ((e.ctrlKey || e.metaKey) && (e.key === 'PageUp' || e.key === 'PageDown')) {
    const nextTab = e.key === 'PageDown'
    if (tabRoot) tabRoot.goToNextOrPreviousTab(nextTab)
    else sendMessageToRoot({ type: 'GoToNextOrPreviousTab', nextTab })
  } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 't') {
    const origin = getLocationUrl().origin
    if (tabRoot) tabRoot.addTab(origin, 'Home', true)
    else sendMessageToRoot({ type: 'AddTab', url: origin, title: 'Home', switchToTab: true })
  } else {
    return
  }
  e.preventDefault()
}

// Hook to manage keyboard shortcuts related to tab management. i.e ctrl+w, ctrl+t, ctrl+pageUp/Down
// Can be used both for the PWA root and inside an iframe. tabRoot must be provided if used for the PWA root.
export const usePwaKeyboardShortcutHandler = (tabRoot?: TabRootStore) => {
  const isPwa = useIsPwa()

  useEffect(() => {
    if (!isPwa) {
      return
    }
    const handler = keyboardShortcutHandler(tabRoot)
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [tabRoot, isPwa])
}

// To be used from inside iframes. Detects document title updates and sends a message to let the PWA root know about the new title.
export const usePwaTitleUpdater = () => {
  const isPwa = useIsPwa()

  useEffect(() => {
    if (!isPwa) {
      return
    }
    const title = document.querySelector('title')
    if (!title) {
      return
    }

    const onTitleChange = () => {
      sendMessageToRoot({ type: 'SetTabTitle', title: document.title })
    }

    new MutationObserver(function (mutations) {
      onTitleChange()
    }).observe(title, { subtree: true, characterData: true, childList: true })

    getHistoryInstance().listen(() => {
      sendMessageToRoot({ type: 'SetTabHref', href: getLocationUrl().href })
    })

    onTitleChange()

    const onLoad = () => sendMessageToRoot({ type: 'LoadingFinished' })

    window.addEventListener('load', onLoad)
    return () => window.removeEventListener('load', onLoad)
  }, [isPwa])
}

// To be used from inside iframes. Detects history status changes and sends a message to let the PWA root know.
export const usePwaHistoryUpdater = (history: History) => {
  useEffect(() => {
    if (!isIFrame()) {
      return
    }
    return history.listen(sendHistoryState)
  }, [history])
}

type MessageToTab =
  | { type: 'RequestUpdateHistoryState' }
  | { type: 'HistoryGoBack' }
  | { type: 'HistoryGoForward' }
  | { type: 'ReloadPage' }

export const sendMessageToTab = (msg: MessageToTab, tab: TabEntry) => {
  const iframe = findTabIframe(tab)
  if (iframe == null) return
  iframe.contentWindow?.postMessage(msg, '*')
}

function sendHistoryState() {
  const history = getHistoryInstance() as MemoryHistory
  const canGoBack = history.index > 0
  const canGoForward = history.length - 1 > history.index
  sendMessageToRoot({ type: 'UpdateHistoryState', canGoBack, canGoForward })
}

// Hook that listens to incoming messages from the root. Should be used in iframes only.
export const useHandleMessageToTab = () => {
  useEffect(() => {
    const onMessage = (rawEvent: MessageEvent) => {
      if (rawEvent.data.source === 'react-devtools-content-script') return

      const event: MessageEvent<MessageToTab> = rawEvent

      if (event.data.type === 'RequestUpdateHistoryState') {
        sendHistoryState()
      } else if (event.data.type === 'HistoryGoBack') {
        getHistoryInstance().goBack()
      } else if (event.data.type === 'HistoryGoForward') {
        getHistoryInstance().goForward()
      } else if (event.data.type === 'ReloadPage') {
        reloadPage()
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])
}
